/**
 * JP Gems & Jewels - Diamond Purchase (inside Hariom ERP)
 * -----------------------------------------------------------------------------
 * Buying side, mirrored from Sales Order:
 *
 *   1. Pick / add a SUPPLIER.
 *   2. Append purchase lines to that supplier's OPEN purchase order, one by one.
 *      Purchase rate auto-fills from the shared price book (Shape+Quality+Slive).
 *   3. CLOSE the PO to finalise it and download ONE combined Diamond Purchase
 *      Order (PDF / Excel). Adding a line again starts a fresh PO.
 *
 * Purchase amount = purchase rate x weight. Classification pick-lists are shared
 * with Sales Order; the price book is shared too, so the founder sees both what
 * is purchased and what is sold from the same combinations.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Boxes, Plus, Trash2, Edit3, FileText, FileSpreadsheet, Upload,
  ChevronLeft, ChevronRight, UserPlus, Lock, CheckCircle2,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { useDiamondPurchaseStore, purchaseTotal, type PurchaseLine, type DiamondPO } from '../../store/diamondPurchaseStore'
import {
  useSalesBookStore, invoiceTotal, SEED_SHAPES, SEED_QUALITIES, SEED_CATEGORIES, SEED_SIEVES, SEED_SIZES, SEED_CODES, SALES_SEED_VERSION,
} from '../../store/salesBookStore'
import { usePriceBookStore, SEED_PRICEBOOK, PRICEBOOK_SEED_VERSION, priceKey } from '../../store/priceBookStore'
import { importPriceChart } from '../../store/importPriceChart'
import { useAuthStore } from '../../store/authStore'

const todayStr = () => new Date().toISOString().slice(0, 10)
const plusDays = (iso: string, days: number) => { const d = new Date(iso || todayStr()); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10) }
const money = (n: number) => '₹' + Math.round(n).toLocaleString('en-IN')
const wt = (n: number) => n.toFixed(2)

interface LineForm {
  date: string; dueDate: string; shape: string; quality: string; category: string
  sieve: string; size: string; code: string; pcs: string; weight: string; purchaseRate: string; remarks: string
}
const emptyForm = (): LineForm => ({
  date: todayStr(), dueDate: plusDays(todayStr(), 45), shape: '', quality: '', category: '',
  sieve: '', size: '', code: '', pcs: '', weight: '', purchaseRate: '', remarks: '',
})

const OptionSelect: React.FC<{
  value: string; onChange: (v: string) => void; options: string[]
  onAdd: (v: string) => void; label: string; transform?: (v: string) => string
}> = ({ value, onChange, options, onAdd, label, transform }) => (
  <div className="flex gap-1 mt-1">
    <select value={value} onChange={e => onChange(e.target.value)} className="form-input flex-1 text-sm">
      <option value="">Select {label}…</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
      {value && !options.includes(value) && <option value={value}>{value}</option>}
    </select>
    <button type="button" title={`Add new ${label}`}
      onClick={() => { const raw = window.prompt(`Add new ${label}:`) || ''; const t = (transform ? transform(raw) : raw).trim(); if (t) { onAdd(t); onChange(t) } }}
      className="shrink-0 w-9 rounded-lg bg-primary-100 text-primary-700 hover:bg-primary-200 text-lg font-semibold leading-none">+</button>
  </div>
)

/**
 * Free-type combo: TYPE any value (one-off sieves that change per customer) AND
 * pick from existing options (datalist). "+" optionally saves it to reuse later.
 */
const ComboSelect: React.FC<{
  value: string; onChange: (v: string) => void; options: string[]
  onAdd: (v: string) => void; label: string; listId: string; transform?: (v: string) => string
}> = ({ value, onChange, options, onAdd, label, listId, transform }) => {
  const known = options.includes(value)
  return (
    <div className="flex gap-1 mt-1">
      <input list={listId} value={value}
        onChange={e => onChange(transform ? transform(e.target.value) : e.target.value)}
        placeholder={`Type or pick ${label}…`}
        className="form-input flex-1 min-w-0 text-sm" />
      <datalist id={listId}>{options.map(o => <option key={o} value={o} />)}</datalist>
      <button type="button" title={value && !known ? `Save “${value}” to the ${label} list` : `Type a new ${label}, then press + to save it`}
        disabled={!value || known}
        onClick={() => { const t = (transform ? transform(value) : value).trim(); if (t && !options.includes(t)) onAdd(t) }}
        className={`shrink-0 w-9 rounded-lg text-lg font-semibold leading-none ${value && !known ? 'bg-primary-100 text-primary-700 hover:bg-primary-200' : 'bg-steel-100 text-steel-300 cursor-not-allowed'}`}>+</button>
    </div>
  )
}

const DiamondPurchase: React.FC = () => {
  const dp = useDiamondPurchaseStore()
  const { orders, suppliers } = dp
  const pick = useSalesBookStore()      // shared classification pick-lists
  const { user } = useAuthStore()
  const priceBook = usePriceBookStore(st => st.book)              // GLOBAL chart baseline (shared fallback)
  const supplierPrices = usePriceBookStore(st => st.supplierPrices) // per-supplier custom purchase prices (reactive)

  useEffect(() => {
    const seed = () => {
      const st = useSalesBookStore.getState()
      const pb = usePriceBookStore.getState()
      if (Object.keys(pb.book).length === 0) { pb.setBook(SEED_PRICEBOOK); pb.setSeedVersion(PRICEBOOK_SEED_VERSION) }
      if (st.seedVersion === SALES_SEED_VERSION) return
      const combos = Object.keys(SEED_PRICEBOOK).map(k => k.split('|'))
      const uni = (a: string[]) => Array.from(new Set(a.filter(Boolean)))
      st.setShapes(uni([...st.shapes, ...SEED_SHAPES, ...combos.map(c => c[0])]))
      st.setQualities(uni([...st.qualities, ...SEED_QUALITIES, ...combos.map(c => c[1])]))
      st.setCategories(uni([...st.categories, ...SEED_CATEGORIES]))
      st.setSieves(SEED_SIEVES)   // exact clean list (replaces the messy ones)
      st.setSizes(SEED_SIZES)     // exact clean list (replaces the messy ones)
      st.setCodes(uni([...st.codes, ...SEED_CODES]))
      st.setSeedVersion(SALES_SEED_VERSION)
    }
    if (useSalesBookStore.persist.hasHydrated()) seed()
    return useSalesBookStore.persist.onFinishHydration(seed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [selected, setSelected] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<LineForm>(emptyForm())
  const [editing, setEditing] = useState<{ poId: string; lineId: string } | null>(null)

  // Auto-fill Purchase Rate. THIS SUPPLIER'S own saved price wins; if none, fall
  // back to the global chart baseline. A price saved for one supplier is never
  // used for another (each supplier can quote a different rate).
  useEffect(() => {
    if (!showModal || !form.shape || !form.quality || !form.sieve || !selected) return
    const combo = priceKey(form.shape, form.quality, form.sieve)
    const supPrice = supplierPrices[selected]?.[combo]
    const purchase = (supPrice && supPrice > 0) ? supPrice : (priceBook[combo]?.p ?? 0)
    if (purchase > 0) setForm(f => (String(purchase) === f.purchaseRate ? f : { ...f, purchaseRate: String(purchase) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.shape, form.quality, form.sieve, showModal, priceBook, supplierPrices, selected])

  // Ensure the selected supplier always has an OPEN PO, so its number shows in
  // the header and the Add Line modal even before the first line is added.
  useEffect(() => {
    if (selected) dp.ensureOpenPO(selected)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected])

  const openPOOf = (sup: string) => orders.find(o => o.supplier === sup && o.status === 'open')
  const closedOf = (sup: string) => orders.filter(o => o.supplier === sup && o.status === 'closed')

  const summary = useMemo(() => {
    const open = orders.filter(o => o.status === 'open')
    const dues = open.reduce((t, o) => t + purchaseTotal(o), 0)
    return { suppliers: suppliers.length, open: open.length, closed: orders.filter(o => o.status === 'closed').length, dues }
  }, [orders, suppliers])

  const importRef = useRef<HTMLInputElement>(null)
  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return
    try { const res = await importPriceChart(file); toast.success(`Imported ${res.rows} rows — ${res.combos} priced combinations`) }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Import failed') }
    e.target.value = ''
  }

  const newSupplier = () => {
    const name = (window.prompt('New supplier name:') || '').trim()
    if (!name) return
    if (suppliers.includes(name)) { toast('Supplier already exists'); setSelected(name); return }
    dp.addSupplier(name); setSelected(name); toast.success(`Supplier "${name}" added`)
  }

  const openNew = () => {
    // Prefill from the LAST line, but leave Sieve/Slive blank — that's the field
    // employees change most on each new purchase line.
    const open = selected ? openPOOf(selected) : undefined
    const last = open?.lines[open.lines.length - 1]
    setForm(last ? {
      date: last.date, dueDate: last.dueDate, shape: last.shape, quality: last.quality, category: last.category,
      sieve: '', size: last.size, code: last.code, pcs: '', weight: '',
      purchaseRate: String(last.purchaseRate), remarks: last.remarks,
    } : emptyForm())
    setEditing(null); setShowModal(true)
  }
  const openEdit = (po: DiamondPO, l: PurchaseLine) => {
    setForm({
      date: l.date, dueDate: l.dueDate, shape: l.shape, quality: l.quality, category: l.category,
      sieve: l.sieve, size: l.size, code: l.code, pcs: String(l.pcs), weight: String(l.weight),
      purchaseRate: String(l.purchaseRate), remarks: l.remarks || '',
    })
    setEditing({ poId: po.id, lineId: l.id }); setShowModal(true)
  }
  const saveComboPrice = () => {
    const r = parseFloat(form.purchaseRate) || 0
    if (!selected) { toast.error('Open a supplier first'); return }
    if (!(form.shape && form.quality && form.sieve && r > 0)) { toast.error('Pick shape, quality, slive and a rate first'); return }
    // Saved PER SUPPLIER — this rate applies to this supplier only, never to others.
    usePriceBookStore.getState().setSupplierPrice(selected, priceKey(form.shape, form.quality, form.sieve), r)
    toast.success(`Purchase price saved for ${selected}: ${form.shape} · ${form.quality} · ${form.sieve} = ₹${r}`)
  }
  const saveLine = () => {
    if (!selected) return
    const purchaseRate = parseFloat(form.purchaseRate) || 0
    if (purchaseRate <= 0) { toast.error('Enter a valid Purchase rate'); return }
    const line: PurchaseLine = {
      id: editing?.lineId || `pl-${Date.now()}`,
      // Due Date, Pcs and Weight removed from the Diamond Purchase invoice (founder).
      // weight defaults to 1 so the purchase value equals the entered rate.
      date: form.date || todayStr(), dueDate: '',
      shape: form.shape.trim(), quality: form.quality.trim(), category: form.category.trim(),
      sieve: form.sieve.trim(), size: form.size.trim(), code: form.code.trim(),
      pcs: 0, weight: 1, purchaseRate, remarks: form.remarks.trim(),
      highlight: editing ? (orders.flatMap(o => o.lines).find(l => l.id === editing.lineId)?.highlight ?? false) : false,
    }
    if (selected && line.shape && line.quality && line.sieve && line.purchaseRate > 0) {
      const k = priceKey(line.shape, line.quality, line.sieve)
      const cur = usePriceBookStore.getState().supplierPrices[selected]?.[k]
      if (!cur) usePriceBookStore.getState().setSupplierPrice(selected, k, line.purchaseRate)
    }
    if (editing) { dp.updateLine(editing.poId, editing.lineId, line); toast.success('Line updated') }
    else { const id = dp.ensureOpenPO(selected); dp.addLine(id, line); toast.success('Line added') }
    setShowModal(false)
  }
  const delLine = (po: DiamondPO, l: PurchaseLine) => { if (window.confirm('Delete this line?')) { dp.removeLine(po.id, l.id); toast.success('Deleted') } }
  const toggleHighlight = (po: DiamondPO, l: PurchaseLine) => dp.updateLine(po.id, l.id, { ...l, highlight: !l.highlight })
  const closePO = (po: DiamondPO) => {
    if (!po.lines.length) { toast.error('Add at least one line before closing'); return }
    if (!window.confirm(`Close purchase order ${po.poNo} for ${po.supplier}?`)) return
    dp.closePO(po.id); dp.ensureOpenPO(po.supplier)   // start a fresh PO (new number)
    toast.success(`${po.poNo} closed — download to record the purchase`)
  }

  const poTotals = (po: DiamondPO) => po.lines.reduce((t, l) => ({ pcs: t.pcs + l.pcs, weight: t.weight + l.weight, amt: t.amt + l.purchaseRate * l.weight }), { pcs: 0, weight: 0, amt: 0 })

  const jpHeader = (doc: jsPDF, W: number) => {
    doc.setFillColor(17, 17, 17); doc.rect(0, 0, W, 26, 'F')
    doc.setDrawColor(232, 190, 140); doc.setLineWidth(0.7)
    doc.lines([[6, 6], [-6, 6], [-6, -6]], 20, 6, [1, 1], 'S', true)
    doc.setTextColor(232, 190, 140); doc.setFont('times', 'bold'); doc.setFontSize(12); doc.text('JP', 17, 14)
    doc.setFont('times', 'bolditalic'); doc.setFontSize(18); doc.text('JP Gems & Jewels', 36, 13)
    doc.setFont('times', 'italic'); doc.setFontSize(8); doc.setTextColor(210, 180, 140); doc.text('Serving Beauty Through Jewellery', 36, 20)
  }

  const downloadPdf = (po: DiamondPO) => {
    if (!po.lines.length) { toast.error('No lines to export'); return }
    const t = poTotals(po)
    const doc = new jsPDF({ orientation: 'landscape' })
    const W = doc.internal.pageSize.getWidth()
    jpHeader(doc, W)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('Diamond Purchase Order', 14, 36)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9)
    doc.text(`Supplier: ${po.supplier}`, 14, 42); doc.text(`PO: ${po.poNo}`, 110, 42)
    doc.text(`Date: ${po.createdDate}`, 170, 42); doc.text(`Status: ${po.status.toUpperCase()}`, 230, 42)
    autoTable(doc, {
      startY: 46,
      head: [['Sr', 'Date', 'Shape', 'Quality', 'Cat', 'Sieve', 'Size', 'Code', 'Purch Rate', 'Remarks']],
      body: po.lines.map((l, i) => [String(i + 1), l.date, l.shape, l.quality, l.category, l.sieve, l.size, l.code, String(l.purchaseRate), l.remarks]),
      foot: [['', '', '', '', '', '', 'TOTAL', '', String(Math.round(t.amt)), '']],
      showFoot: 'lastPage',   // grand TOTAL prints ONCE, on the final page
      styles: { fontSize: 7, cellPadding: 1 },
      headStyles: { fillColor: [31, 56, 100] },
      footStyles: { fillColor: [17, 17, 17], textColor: [232, 190, 140], fontStyle: 'bold' },
      // Flagged lines are shaded red so they stand out.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didParseCell: (d: any) => { if (d.section === 'body' && po.lines[d.row.index]?.highlight) d.cell.styles.fillColor = [254, 226, 226] },
    })
    const y = (doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY || 60
    doc.setFontSize(10); doc.setTextColor(0, 0, 0); doc.text(`Total Purchase: ${money(t.amt)}`, 14, y + 8)
    doc.setFontSize(9); doc.setTextColor(90, 90, 90); doc.text(`Generated by: ${user?.name || '-'}`, 120, y + 8)
    doc.save(`JP_${po.poNo}_${po.supplier.replace(/\s+/g, '_')}.pdf`)
    toast.success('Purchase Order PDF downloaded')
  }

  const downloadExcel = (po: DiamondPO) => {
    if (!po.lines.length) { toast.error('No lines to export'); return }
    const t = poTotals(po)
    const rows: Record<string, string | number>[] = po.lines.map((l, i) => ({
      Flag: l.highlight ? '★ FLAG' : '', Sr: i + 1, Date: l.date, Shape: l.shape, Quality: l.quality, Category: l.category,
      Sieve: l.sieve, Size: l.size, Code: l.code, 'Purchase Rate': l.purchaseRate, Remarks: l.remarks,
    }))
    rows.push({ Flag: '', Sr: '', Date: '', Shape: '', Quality: '', Category: '', Sieve: '', Size: '', Code: 'TOTAL', 'Purchase Rate': Math.round(t.amt), Remarks: '' })
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)
    XLSX.utils.sheet_add_aoa(ws, [[`${po.supplier}  |  PO ${po.poNo}  |  ${po.createdDate}  |  ${po.status.toUpperCase()}`]], { origin: 'A1' })
    XLSX.utils.book_append_sheet(wb, ws, po.poNo.slice(0, 31))
    XLSX.writeFile(wb, `JP_${po.poNo}_${po.supplier.replace(/\s+/g, '_')}.xlsx`)
    toast.success('Purchase Order Excel downloaded')
  }

  // ---- Estimate & Margin (compare Diamond Purchase vs Sales Order) ----
  const est = useMemo(() => {
    const totalPurchase = orders.reduce((t, o) => t + purchaseTotal(o), 0)
    const totalSales = pick.invoices.reduce((t, i) => t + invoiceTotal(i), 0)
    const margin = totalSales - totalPurchase
    const marginPct = totalPurchase > 0 ? (margin / totalPurchase) * 100 : 0
    const combos = Object.entries(priceBook)
      .filter(([, v]) => v.p > 0 && v.s > 0)
      .map(([k, v]) => { const [sh, q, sl] = k.split('|'); const m = v.s - v.p; return { combo: `${sh} / ${q} / ${sl}`, p: v.p, s: v.s, m, pct: v.p > 0 ? (m / v.p) * 100 : 0 } })
      .sort((a, b) => b.m - a.m)
    return { totalPurchase, totalSales, margin, marginPct, combos }
  }, [orders, pick.invoices, priceBook])

  const rs = (n: number) => 'Rs. ' + Math.round(n).toLocaleString('en-IN')
  const downloadEstimatePdf = () => {
    const doc = new jsPDF()
    const W = doc.internal.pageSize.getWidth()
    jpHeader(doc, W)
    doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.text('Estimate & Margin Report', 14, 36)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(`Date: ${todayStr()}`, 165, 36)
    doc.setFontSize(10)
    doc.text(`Total Diamond Purchase: ${rs(est.totalPurchase)}`, 14, 46)
    doc.text(`Total Sales: ${rs(est.totalSales)}`, 14, 52)
    doc.setFont('helvetica', 'bold'); doc.text(`Gross Profit (Sales - Purchase): ${rs(est.margin)}  (${est.marginPct.toFixed(1)}%)`, 14, 58)
    autoTable(doc, {
      startY: 64,
      head: [['Combination (Shape / Quality / Slive)', 'Purchase/ct', 'Sale/ct', 'Margin/ct', 'Margin %']],
      body: est.combos.map(c => [c.combo, String(c.p), String(c.s), String(c.m), c.pct.toFixed(1) + '%']),
      styles: { fontSize: 8, cellPadding: 1.4 },
      headStyles: { fillColor: [31, 56, 100] },
    })
    doc.save(`JP_Estimate_Report_${todayStr()}.pdf`)
    toast.success('Estimate report PDF downloaded')
  }
  const downloadEstimateExcel = () => {
    const wb = XLSX.utils.book_new()
    const summary = [
      { Field: 'Total Diamond Purchase (Rs)', Value: Math.round(est.totalPurchase) },
      { Field: 'Total Sales (Rs)', Value: Math.round(est.totalSales) },
      { Field: 'Gross Profit (Rs)', Value: Math.round(est.margin) },
      { Field: 'Margin %', Value: Number(est.marginPct.toFixed(1)) },
    ]
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summary), 'Summary')
    const combos = est.combos.map(c => ({ Combination: c.combo, 'Purchase/ct': c.p, 'Sale/ct': c.s, 'Margin/ct': c.m, 'Margin %': Number(c.pct.toFixed(1)) }))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(combos.length ? combos : [{ Combination: 'No priced combinations yet' }]), 'Margins by Combination')
    XLSX.writeFile(wb, `JP_Estimate_Report_${todayStr()}.xlsx`)
    toast.success('Estimate report Excel downloaded')
  }

  const comboKey = form.shape && form.quality && form.sieve ? priceKey(form.shape, form.quality, form.sieve) : ''
  // This supplier's saved purchase price (if any); else the global chart baseline.
  const supComboPrice = comboKey && selected ? supplierPrices[selected]?.[comboKey] : undefined
  const comboPurchase = (supComboPrice && supComboPrice > 0) ? supComboPrice : (comboKey ? (priceBook[comboKey]?.p ?? 0) : 0)
  const activeOpen = selected ? openPOOf(selected) : undefined
  const activePoNo = activeOpen ? activeOpen.poNo : (selected ? `JP-DP-${String(dp.poSeq + 1).padStart(4, '0')}` : '')

  function renderModal() {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-steel-800">{editing ? 'Edit Line' : 'Add Line'} — {selected}
              <span className="ml-2 font-mono text-sm text-primary-700">{activePoNo}</span></h3>
            <button onClick={() => setShowModal(false)} className="text-steel-400 hover:text-steel-700 text-2xl leading-none">×</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-steel-600">Date</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="form-input w-full mt-1 text-sm" /></div>
            <div><label className="text-xs font-medium text-steel-600">Shape</label>
              <OptionSelect value={form.shape} onChange={v => setForm({ ...form, shape: v })} options={pick.shapes} onAdd={pick.addShape} label="shape" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Quality</label>
              <ComboSelect value={form.quality} onChange={v => setForm({ ...form, quality: v })} options={pick.qualities} onAdd={pick.addQuality} label="quality" listId="dp-quality-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Category</label>
              <ComboSelect value={form.category} onChange={v => setForm({ ...form, category: v })} options={pick.categories} onAdd={pick.addCategory} label="category" listId="dp-category-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Sieve / Slive</label>
              <ComboSelect value={form.sieve} onChange={v => setForm({ ...form, sieve: v })} options={pick.sieves} onAdd={pick.addSieve} label="sieve" listId="dp-sieve-list" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Size</label>
              <OptionSelect value={form.size} onChange={v => setForm({ ...form, size: v })} options={pick.sizes} onAdd={pick.addSize} label="size" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Code</label>
              <OptionSelect value={form.code} onChange={v => setForm({ ...form, code: v })} options={pick.codes} onAdd={pick.addCode} label="code" transform={x => x.toUpperCase()} /></div>
            <div><label className="text-xs font-medium text-steel-600">Purchase Rate ₹ * <span className="text-steel-400 font-normal">(auto)</span></label>
              <div className="flex gap-1 mt-1">
                <input type="number" value={form.purchaseRate} onChange={e => setForm({ ...form, purchaseRate: e.target.value })} placeholder="auto from price book" className="form-input flex-1 text-sm" />
                <button type="button" title={`Save this rate for ${selected || 'this supplier'} · Shape · Quality · Slive`} onClick={saveComboPrice} className="shrink-0 w-9 rounded-lg bg-primary-100 text-primary-700 hover:bg-primary-200 text-lg font-semibold leading-none">+</button>
              </div>
              {comboKey && (comboPurchase > 0
                ? <p className="text-[11px] text-green-600 mt-1">✓ Auto-filled{supComboPrice && supComboPrice > 0 ? ` (saved for ${selected})` : ' (chart baseline)'}</p>
                : <p className="text-[11px] text-amber-600 mt-1">No saved purchase price for {selected || 'this supplier'} · this Shape·Quality·Slive — type it once, then press + to remember it for this supplier.</p>)}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-steel-600">Remarks (shown on the PO)</label>
            <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder="e.g. sample, on approval…" className="form-input w-full mt-1 text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowModal(false)} className="btn-secondary text-sm">Cancel</button>
            <button onClick={saveLine} className="btn-primary text-sm">{editing ? 'Save Changes' : 'Add Line'}</button>
          </div>
        </div>
      </div>
    )
  }

  // ================= LEVEL 2 — one supplier's purchase order =================
  if (selected) {
    const open = openPOOf(selected)
    const closed = closedOf(selected)
    const t = open ? poTotals(open) : { pcs: 0, weight: 0, amt: 0 }
    return (
      <div className="space-y-5">
        <div className="page-header">
          <div className="flex items-center gap-3">
            <button onClick={() => setSelected(null)} className="p-1.5 rounded-lg hover:bg-steel-100 text-steel-600" title="Back to suppliers"><ChevronLeft size={20} /></button>
            <div>
              <h1 className="page-title flex items-center gap-2"><Boxes size={20} className="text-primary-600" /> {selected}</h1>
              <p className="page-subtitle">Diamond purchase order — add purchases one by one, then close to generate the PO</p>
            </div>
          </div>
          <button onClick={openNew} className="btn-primary text-sm flex items-center gap-2"><Plus size={15} /> Add Line</button>
        </div>

        {open ? (
          <div className="card p-0 overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-steel-200 bg-steel-50">
              <div className="flex items-center gap-3">
                <span className="text-xs text-steel-500">PO No</span>
                <input value={open.poNo} onChange={e => dp.setPoNo(open.id, e.target.value)} className="font-mono text-sm font-semibold text-primary-700 bg-white border border-steel-200 rounded px-2 py-0.5 w-36" />
                <span className="text-xs text-steel-500">· {open.createdDate}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">OPEN</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(open)} className="text-xs px-2.5 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-1"><FileText size={13} /> PDF</button>
                <button onClick={() => downloadExcel(open)} className="text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 flex items-center gap-1"><FileSpreadsheet size={13} /> Excel</button>
                <button onClick={() => closePO(open)} className="text-xs px-2.5 py-1.5 bg-steel-800 text-white rounded-lg hover:bg-steel-900 flex items-center gap-1"><Lock size={13} /> Close &amp; Generate</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-steel-50 border-b border-steel-200">
                  {['#', 'Date', 'Shape', 'Quality', 'Cat', 'Sieve', 'Size', 'Code', 'Purch ₹', 'Remarks', 'Flag', ''].map((h, i) => (
                    <th key={i} className={clsx('table-header whitespace-nowrap', i === 8 && 'text-right', i === 10 && 'text-center')}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {open.lines.length === 0 ? (
                    <tr><td colSpan={12} className="text-center text-steel-400 py-10">No lines yet. Click “Add Line” to start this purchase order.</td></tr>
                  ) : open.lines.map((l, i) => (
                    <tr key={l.id} className={clsx('table-row-hover', l.highlight && 'bg-red-50')}>
                      <td className="table-cell text-center text-steel-400">{i + 1}</td>
                      <td className="table-cell whitespace-nowrap text-steel-500">{l.date}</td>
                      <td className="table-cell font-medium text-steel-800 whitespace-nowrap">{l.shape || '—'}</td>
                      <td className="table-cell whitespace-nowrap">{l.quality || '—'}</td>
                      <td className="table-cell whitespace-nowrap">{l.category || '—'}</td>
                      <td className="table-cell whitespace-nowrap">{l.sieve || '—'}</td>
                      <td className="table-cell whitespace-nowrap">{l.size || '—'}</td>
                      <td className="table-cell whitespace-nowrap">{l.code || '—'}</td>
                      <td className="table-cell text-right font-semibold text-primary-700">{l.purchaseRate.toLocaleString('en-IN')}</td>
                      <td className="table-cell max-w-[180px] truncate text-steel-600" title={l.remarks}>{l.remarks || '—'}</td>
                      <td className="table-cell text-center">
                        <button onClick={() => toggleHighlight(open, l)} title="Flag this line to clarify with the customer (shows on downloads)" className={clsx('px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap', l.highlight ? 'bg-red-600 text-white' : 'bg-steel-100 text-steel-500 hover:bg-red-100 hover:text-red-600')}>{l.highlight ? '● Flagged' : 'Flag'}</button>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => openEdit(open, l)} className="p-1 text-steel-500 hover:text-primary-600 hover:bg-primary-50 rounded" title="Edit"><Edit3 size={13} /></button>
                          {user?.role === 'admin' && (
                            <button onClick={() => delLine(open, l)} className="p-1 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete (founder only)"><Trash2 size={13} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {open.lines.length > 0 && (
                  <tfoot>
                    <tr className="bg-steel-900 text-white font-semibold">
                      <td className="px-3 py-2 text-right whitespace-nowrap" colSpan={8}>TOTAL ({open.lines.length} lines)</td>
                      <td className="px-3 py-2 text-right text-amber-300 whitespace-nowrap">{money(t.amt)}</td>
                      <td className="px-3 py-2" colSpan={3}></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {open.lines.length > 0 && (
              <div className="px-4 py-2 flex justify-end">
                <button onClick={openNew} className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"><Plus size={14} /> Add next line</button>
              </div>
            )}
          </div>
        ) : (
          <div className="card text-center py-10 text-steel-400 text-sm">Preparing purchase order…</div>
        )}

        {closed.length > 0 && (
          <div className="card p-0 overflow-hidden">
            <p className="px-4 py-2.5 text-sm font-semibold text-steel-700 border-b border-steel-200 flex items-center gap-2"><CheckCircle2 size={15} className="text-green-600" /> Closed Purchase Orders ({closed.length})</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-steel-50 border-b border-steel-200">
                  <th className="table-header">PO No</th><th className="table-header">Closed</th>
                  <th className="table-header text-center">Lines</th><th className="table-header text-right">Total Purchase</th>
                  <th className="table-header text-center">Download / Remove</th>
                </tr></thead>
                <tbody>
                  {closed.map(po => (
                    <tr key={po.id} className="table-row-hover">
                      <td className="table-cell font-mono text-xs font-medium text-primary-700">{po.poNo}</td>
                      <td className="table-cell text-xs text-steel-500">{po.closedDate}</td>
                      <td className="table-cell text-center">{po.lines.length}</td>
                      <td className="table-cell text-right font-bold">{money(purchaseTotal(po))}</td>
                      <td className="table-cell text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => downloadPdf(po)} className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1"><FileText size={11} /> PDF</button>
                          <button onClick={() => downloadExcel(po)} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 flex items-center gap-1"><FileSpreadsheet size={11} /> Excel</button>
                          {user?.role === 'admin' && (
                            <button onClick={() => { if (window.confirm(`Delete ${po.poNo}?`)) { dp.removePO(po.id); toast.success('Deleted') } }} title="Delete (founder only)" className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={13} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && renderModal()}
      </div>
    )
  }

  // ================= LEVEL 1 — suppliers list =================
  const rows = suppliers.map(sup => {
    const open = openPOOf(sup)
    return { sup, open, amt: open ? purchaseTotal(open) : 0, lines: open ? open.lines.length : 0, closed: closedOf(sup).length }
  })

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2"><Boxes size={22} className="text-primary-600" /> Diamond Purchase</h1>
          <p className="page-subtitle">JP Gems &amp; Jewels — one running purchase order per supplier; prices auto-fill, then close &amp; generate the PO</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === 'admin' && (<>
            <button onClick={downloadEstimatePdf} title="Estimate & margin report (founder)" className="btn-secondary text-sm flex items-center gap-2"><FileText size={14} /> Estimate PDF</button>
            <button onClick={downloadEstimateExcel} title="Estimate & margin report (founder)" className="btn-secondary text-sm flex items-center gap-2"><FileSpreadsheet size={14} /> Estimate Excel</button>
          </>)}
          <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          <button onClick={() => importRef.current?.click()} title="Import price chart (Shape · Quality · Slive → purchase/sale)" className="btn-secondary text-sm flex items-center gap-2"><Upload size={14} /> Import Prices</button>
          <button onClick={newSupplier} className="btn-primary text-sm flex items-center gap-2"><UserPlus size={15} /> New Supplier</button>
        </div>
      </div>


      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card py-3 border-l-4 border-l-blue-500"><p className="text-xs text-steel-500">Suppliers</p><p className="text-2xl font-bold">{summary.suppliers}</p></div>
        <div className="card py-3 border-l-4 border-l-amber-500"><p className="text-xs text-steel-500">Open POs</p><p className="text-2xl font-bold">{summary.open}</p></div>
        <div className="card py-3 border-l-4 border-l-primary-500"><p className="text-xs text-steel-500">Open Purchase Amt</p><p className="text-2xl font-bold text-primary-600">{money(summary.dues)}</p></div>
        <div className="card py-3 border-l-4 border-l-green-500"><p className="text-xs text-steel-500">Closed POs</p><p className="text-2xl font-bold">{summary.closed}</p></div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-steel-50 border-b border-steel-200">
              <th className="table-header">Supplier</th><th className="table-header">Open PO</th>
              <th className="table-header text-center">Open Lines</th><th className="table-header text-right">Open Purchase Amt</th>
              <th className="table-header text-center">Closed</th><th className="table-header text-center">Action</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-steel-400 py-10">No suppliers yet. Click “New Supplier” to add your first one.</td></tr>
              ) : rows.map(r => (
                <tr key={r.sup} className="table-row-hover cursor-pointer" onClick={() => setSelected(r.sup)}>
                  <td className="table-cell font-medium text-steel-800">{r.sup}</td>
                  <td className="table-cell font-mono text-xs text-primary-700">{r.open ? r.open.poNo : '—'}</td>
                  <td className="table-cell text-center">{r.lines || '—'}</td>
                  <td className="table-cell text-right font-semibold">{r.amt ? money(r.amt) : '—'}</td>
                  <td className="table-cell text-center">{r.closed || '—'}</td>
                  <td className="table-cell text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={e => { e.stopPropagation(); setSelected(r.sup) }} className="text-xs px-2.5 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 inline-flex items-center gap-1">Open <ChevronRight size={12} /></button>
                      {user?.role === 'admin' && (
                        <button onClick={e => { e.stopPropagation(); if (window.confirm(`Delete supplier "${r.sup}" and ALL its purchase orders? This cannot be undone.`)) { dp.deleteSupplier(r.sup); toast.success('Supplier deleted') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-red-50 rounded" title="Delete supplier (founder only)"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default DiamondPurchase
